import CalculatorTextInput from "./CalculatorForm/CalculatorTextInput/CalculatorTextInput"
import CalculatorSelectInput from "./CalculatorForm/CalculatorSelectInput/CalculatorSelectInput"

export interface iOption {
  display_name: string
  value: any
}

export interface iCALC_STRUCTURE_ITEM_INPUT {
  Component: typeof CalculatorTextInput | typeof CalculatorSelectInput
  label: string
  placeholder?: string
  type?: string
  error?: (value: any) => string | null
  warning?: (value: any) => string | null
  unit?: string
  showFeedback?: boolean
  shortInfo: string
  extendedInfo: string
  options?: iOption[]
}

export interface iGDM_CALC_STRUCTURE {
  [id: string]: iCALC_STRUCTURE_ITEM_INPUT
}

const Structure = {
  maternal_age: {
    Component: CalculatorTextInput,
    label: "Maternal Age",
    placeholder: "Enter your observation",
    type: "number",
    error: (value: string) => {
      if (!value) return null
      const value_name = "age"
      const value_unit = "years"
      const min = 12
      const max = 60
      const float_value = parseFloat(value)
      if (float_value < min)
        return `The ${value_name} must be at least ${min} ${value_unit}`
      if (float_value > max)
        return `The ${value_name} can be at most ${max} ${value_unit}`
    },
    warning: (value: string) => {
      if (!value) return "Please enter a valid value"
      return null
    },
    unit: "Years",
    showFeedback: true,
    shortInfo: "The valid age range is 12 to 60 years",
    extendedInfo: "This is the age of the mother antepartum.",
  },

  height: {
    Component: CalculatorTextInput,
    label: "Height",
    placeholder: "Enter your observation",
    type: "number",
    error: (value: string) => {
      if (!value) return null
      const value_name = "height"
      const value_unit = "cm"
      const min = 120
      const max = 250
      const float_value = parseFloat(value)
      if (float_value < min)
        return `The ${value_name} must be at least ${min} ${value_unit}`
      if (float_value > max)
        return `The ${value_name} can be at most ${max} ${value_unit}`
    },
    warning: (value: string) => {
      if (!value) return "Please enter a valid value"
      return null
    },
    unit: "Centimeters (cm)",
    showFeedback: true,
    shortInfo: "The valid height range is 120 to 250 cm",
    extendedInfo: "This is the self-reported height in centimeters (cm)",
  },

  pre_pregnancy_weight: {
    Component: CalculatorTextInput,
    label: "Weight",
    placeholder: "Enter your observation",
    type: "number",
    error: (value: string) => {
      if (!value) return null
      const value_name = "weight"
      const value_unit = "kg"
      const min = 20
      const max = 300
      const float_value = parseFloat(value)
      if (float_value < min)
        return `The ${value_name} must be at least ${min} ${value_unit}`
      if (float_value > max)
        return `The ${value_name} can be at most ${max} ${value_unit}`
    },
    warning: (value: string) => {
      if (!value) return "Please enter a valid value"
      return null
    },
    unit: "Kilograms",
    showFeedback: true,
    shortInfo: "The valid weight range is 20 to 300 kg",
    extendedInfo:
      "Weight measured at the initial hospital visit should be used to calculate the BMI.",
  },

  ethnicity: {
    Component: CalculatorSelectInput,
    label: "Ethnicity",
    showFeedback: true,
    shortInfo: "Select your observation",
    extendedInfo: `The categorising of ethnicity is adapted 
      from the Australian Standard Classification of Cultural and Ethnic Groups (ASCCEG), 2019.
      `,
    options: [
      {
        display_name: "Caucasian",
        value: 0,
      },
      {
        display_name: "Southern and Central Asian",
        value: 1,
      },
      {
        display_name: "SE and NE Asian",
        value: 2,
      },
      {
        display_name: "North African, Middle Eastern & Sub-Saharan African",
        value: 3,
      },
      {
        display_name: "Oceanian not Anglo-Australian",
        value: 4,
      },
      {
        display_name: "Other",
        value: 5,
      },
    ],
  },

  family_history_of_diabetes: {
    Component: CalculatorSelectInput,
    label: "Family History of Diabetes",
    showFeedback: true,
    shortInfo: "Select your observation",
    extendedInfo:
      "This includes a history of any type of diabetes in blood relatives",
    options: [
      {
        display_name: "Yes",
        value: 1,
      },
      {
        display_name: "No",
        value: 0,
      },
    ],
  },

  past_history_of_gestational_diabetes: {
    Component: CalculatorSelectInput,
    label: "Past history of gestational diabetes",
    showFeedback: true,
    shortInfo: "Select your observation",
    extendedInfo:
      "This indicates a history of gestational diabetes in previous pregnancies",
    options: [
      {
        display_name: "Yes",
        value: 1,
      },
      {
        display_name: "No",
        value: 0,
      },
    ],
  },

  poor_obstetric_outcome: {
    Component: CalculatorSelectInput,
    label: "Poor obstetric outcome",
    showFeedback: true,
    shortInfo: "Select your observation",
    extendedInfo: `
      History of any one or more of the following in a previous pregnancy: a macrosomic baby, shoulder dystocia, 
      preeclampsia or eclampsia, antepartum haemorrhage or intrauterine growth restriction.
    `,
    options: [
      {
        display_name: "Yes",
        value: 1,
      },
      {
        display_name: "No",
        value: 0,
      },
    ],
  },
} as iGDM_CALC_STRUCTURE

export const inputValueToDisplayName = (key: string, value: any) => {
  if (!Structure[key]) return value
  const options = Structure[key].options
  if (!options) return value
  return options.filter(item => item.value === parseInt(value))[0].display_name
}

export default Structure
